import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useShipmentApi} from "../../../../../app/api/shipment";
import {toast} from "react-toastify";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import {isAxiosError} from "axios";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import {ICustomsEtaAction} from "../../../../../app/interfaces/shipment/ICustomsEtaAction";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import CustomsEtaForm from "./CustomsEtaForm/CustomsEtaForm";
import {IError} from "../../../../../app/interfaces/response/IResponse";
import {BLErrorCode} from "../../../../../app/enums/BLErrorCode";

type CustomsEtaProps = {
    id: string;
    sId: string;
    close?: () => void;
}

const CustomsEta: React.FC<CustomsEtaProps> = ({
                                                   id,
                                                   sId,
                                                   close
                                               }) => {
    const {t} = useTranslation();
    const [disabled, setDisabled] = useState<boolean>(false);
    const [model, setModel] = useState<ICustomsEtaAction | null>(null);
    const [errors, setErrors] = useState<Array<IError>>([]);

    const {
        getCustomsETAData: {
            query: getCustomsETAData,
            isLoading,
            cancel: cancelGetCustomsETAData
        },
        changeCustomsETA: {
            mutation: changeCustomsETA,
            cancel: cancelChangeCustomsETA
        }
    } = useShipmentApi();

    useEffect(() => {
        if (id === '' || sId === '') {
            if (close) {
                close()
            } else {
                return;
            }
        }

        (async () => {
            try {
                const response = await getCustomsETAData(id, sId);
                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch {
                if (close) {
                    close();
                } else {
                    toast.error<string>('Unable to get eta data');
                }
            }
        })();

        return () => {
            cancelChangeCustomsETA();
            cancelGetCustomsETAData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sId, id]);

    const save = async (model: ICustomsEtaAction): Promise<void> => {
        setDisabled(prev => !prev);
        setErrors([]);
        try {
            const response = await changeCustomsETA(model);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRows',
                        rows: [
                            {
                                id: sId,
                                isRowLoading: true
                            }
                        ]
                    }
                }));

                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("shared.labels.shipment")} ${sId}`}/>);
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setErrors(err.response.data.errors);
                } else if (close) {
                    close();
                    toast.error(err.message);
                }
            }
        }

        setDisabled(prev => !prev);
    }

    if (isLoading || model === null) {
        return (
            <FormLoadingScreen height={`200px`} style={{padding: '5px'}}/>
        )
    }

    return (
        <div className={'h100 w100'}>
            <div className={'dialog-default-content-container'}>
                {errors.length > 0 && errors.map((err, index) => {
                    switch (err.errorCode) {
                        case BLErrorCode.InvalidDate:
                            return (
                                <div key={err.errorCode} className={`customs-eta-error-${index}`}>
                                    {t("shipments.errors.add-customs-eta-invalid-date")}
                                </div>
                            );
                    }

                    return null;
                })}
                <CustomsEtaForm model={model}
                                disabled={disabled}
                                save={save}/>
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={disabled}
                        form='change-customs-eta-form'
                        type='submit'
                        style={{minWidth: '150px'}}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.submit")}
                </Button>
            </div>
        </div>
    );
};

export default CustomsEta;